import { useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Download, Search, SlidersHorizontal, X } from 'lucide-react';
import { enumLabel, DataTable, EmptyState, Money, StatusBadge, relativeTime, type Column } from '@riskpay/ui-components';
import type { ListResponse, Payment } from '@riskpay/types';
import { api, useAuth } from '../lib/client';

/* ==========================================================================
 *  Transaction explorer
 *
 *  Keyset pagination, not offset: the cursor is the timestamp of the last row,
 *  so a new payment arriving mid-browse cannot shift the page under the reader
 *  and duplicate a row. "Load more" appends; changing a filter resets.
 * ========================================================================== */

const STATUSES = ['SUCCESS', 'PENDING', 'FAILED', 'REFUNDED', 'PARTIALLY_REFUNDED', 'DISPUTED'];
const METHODS = ['UPI', 'UPI_INTENT', 'CREDIT_CARD', 'DEBIT_CARD', 'NET_BANKING', 'WALLET'];

export function Transactions() {
  const mode = useAuth((s) => s.session?.mode ?? 'TEST');
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<string[]>([]);
  const [method, setMethod] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [pages, setPages] = useState<string[]>([]);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const filters = {
    search: search.trim() || undefined,
    status: status.length ? status.join(',') : undefined,
    method: method || undefined,
    from: from ? new Date(from).toISOString() : undefined,
    to: to ? new Date(`${to}T23:59:59`).toISOString() : undefined,
  };

  const cursor = pages[pages.length - 1];

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ['transactions', mode, filters, cursor],
    queryFn: () => api.get<ListResponse<Payment>>('/transactions', { ...filters, cursor, limit: 25 }),
    placeholderData: keepPreviousData,
  });

  const resetPaging = () => setPages([]);
  const activeFilters = status.length + (method ? 1 : 0) + (from ? 1 : 0) + (to ? 1 : 0);

  const columns: Column<Payment>[] = [
    {
      key: 'id',
      header: 'Transaction',
      render: (t) => (
        <div>
          <p className="numeral text-xs text-content">{t.id}</p>
          {t.merchant_order_id && <p className="numeral mt-0.5 text-2xs text-content-faint">{t.merchant_order_id}</p>}
        </div>
      ),
    },
    {
      key: 'method',
      header: 'Method',
      render: (t) => (
        <div>
          <p className="text-xs text-content-muted">{enumLabel(t.payment_method)}</p>
          {(t.card?.last4 || t.upi?.rrn) && (
            <p className="numeral mt-0.5 text-2xs text-content-faint">
              {t.card?.last4 ? `•••• ${t.card.last4}` : t.upi?.rrn}
            </p>
          )}
        </div>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (t) => (
        <div>
          <StatusBadge status={t.status} />
          {t.failure && <p className="mt-1 text-2xs text-content-faint">{t.failure.code}</p>}
        </div>
      ),
    },
    { key: 'amount', header: 'Amount', align: 'right', render: (t) => <Money amount={t.amount} currency={t.currency} className="text-xs" /> },
    { key: 'fee', header: 'Fee', align: 'right', render: (t) => <Money amount={t.fee.amount} className="text-xs text-content-subtle" /> },
    { key: 'net', header: 'Net', align: 'right', render: (t) => <Money amount={t.net_amount.amount} className="text-xs text-content-muted" /> },
    {
      key: 'when',
      header: 'Created',
      align: 'right',
      render: (t) => (
        <span className="text-xs text-content-subtle" title={new Date(t.created_at).toLocaleString('en-IN')}>
          {relativeTime(t.created_at)}
        </span>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      {/* ---- Toolbar ---------------------------------------------------- */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative min-w-[240px] flex-1">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-content-faint" />
          <input
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              resetPaging();
            }}
            placeholder="Transaction ID, order ID, UPI RRN, email or phone"
            className="input pl-8"
            aria-label="Search transactions"
          />
        </div>

        <button
          type="button"
          onClick={() => setFiltersOpen((v) => !v)}
          className="btn btn-secondary"
          aria-expanded={filtersOpen}
        >
          <SlidersHorizontal className="h-4 w-4" />
          Filters
          {activeFilters > 0 && <span className="badge badge-success ml-1">{activeFilters}</span>}
        </button>

        <button
          type="button"
          onClick={() => api.download('/transactions/export', `riskpay-transactions-${Date.now()}.csv`, { from: filters.from, to: filters.to })}
          className="btn btn-secondary"
        >
          <Download className="h-4 w-4" />
          Export CSV
        </button>
      </div>

      {filtersOpen && (
        <div className="panel animate-slide-down p-4">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <div className="sm:col-span-2">
              <p className="mb-1.5 text-2xs uppercase tracking-wider text-content-subtle">Status</p>
              <div className="flex flex-wrap gap-1.5">
                {STATUSES.map((value) => {
                  const on = status.includes(value);
                  return (
                    <button
                      key={value}
                      type="button"
                      onClick={() => {
                        setStatus((current) => (on ? current.filter((s) => s !== value) : [...current, value]));
                        resetPaging();
                      }}
                      aria-pressed={on}
                      className={`badge ${on ? 'badge-success' : 'badge-neutral'} cursor-pointer`}
                    >
                      {value.replace(/_/g, ' ').toLowerCase()}
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <label htmlFor="f-method" className="mb-1.5 block text-2xs uppercase tracking-wider text-content-subtle">
                Method
              </label>
              <select
                id="f-method"
                value={method}
                onChange={(e) => {
                  setMethod(e.target.value);
                  resetPaging();
                }}
                className="input"
              >
                <option value="">All methods</option>
                {METHODS.map((m) => (
                  <option key={m} value={m}>
                    {m.replace(/_/g, ' ')}
                  </option>
                ))}
              </select>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div>
                <label htmlFor="f-from" className="mb-1.5 block text-2xs uppercase tracking-wider text-content-subtle">
                  From
                </label>
                <input id="f-from" type="date" value={from} onChange={(e) => { setFrom(e.target.value); resetPaging(); }} className="input" />
              </div>
              <div>
                <label htmlFor="f-to" className="mb-1.5 block text-2xs uppercase tracking-wider text-content-subtle">
                  To
                </label>
                <input id="f-to" type="date" value={to} onChange={(e) => { setTo(e.target.value); resetPaging(); }} className="input" />
              </div>
            </div>
          </div>

          {activeFilters > 0 && (
            <button
              type="button"
              onClick={() => {
                setStatus([]);
                setMethod('');
                setFrom('');
                setTo('');
                resetPaging();
              }}
              className="btn btn-ghost mt-4 h-7 px-2 text-2xs"
            >
              <X className="h-3 w-3" />
              Clear filters
            </button>
          )}
        </div>
      )}

      <DataTable
        columns={columns}
        rows={data?.data ?? []}
        rowKey={(t) => t.id}
        loading={isLoading}
        empty={
          <EmptyState
            title="No transactions match these filters"
            description="Try widening the date range, or clear the filters to see everything in this mode."
          />
        }
      />

      <div className="flex items-center justify-between">
        <p className="text-2xs text-content-faint">
          {data?.data.length ?? 0} shown{isFetching && ' · refreshing'}
        </p>
        <div className="flex gap-2">
          {pages.length > 0 && (
            <button type="button" onClick={() => setPages((p) => p.slice(0, -1))} className="btn btn-secondary h-8 text-xs">
              Previous
            </button>
          )}
          {data?.has_more && data.next_cursor && (
            <button
              type="button"
              onClick={() => setPages((p) => [...p, data.next_cursor!])}
              className="btn btn-secondary h-8 text-xs"
            >
              Next
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
